/**
* @license Apache-2.0
*
* Copyright (c) 2023 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/**
* Apply a function to elements in a three-dimensional nested input array and assign results to elements in a new three-dimensional nested output array.
*
* @module @stdlib/array-base-map3d
*
* @example
* import ones3d from '@stdlib/array-base-ones3d';
* import map3d from '@stdlib/array-base-map3d';
*
* function scale( x ) {
*     return x * 10.0;
* }
*
* var shape = [ 1, 2, 2 ];
*
* var x = ones3d( shape );
* var y = map3d( x, shape, scale );
* // returns [ [ [ 10.0, 10.0 ], [ 10.0, 10.0 ] ] ]
*
* @example
* import ones3d from '@stdlib/array-base-ones3d';
* import zeros3d from '@stdlib/array-base-zeros3d';
* import map3d from '@stdlib/array-base-map3d';
*
* function scale( x ) {
*     return x * 10.0;
* }
*
* var shape = [ 1, 2, 2 ];
*
* var x = ones3d( shape );
* var y = zeros3d( shape );
*
* var out = map3d.assign( x, y, shape, scale );
* // returns [ [ [ 10.0, 10.0 ], [ 10.0, 10.0 ] ] ]
*
* var bool = ( out === y );
* // returns true
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import main from './main.js';
import assign from './assign.js';


// MAIN //

setReadOnly( main, 'assign', assign );


// EXPORTS //

export default main;
